import { Bell, Info } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { Where } from '@/core/api';
import { useUserGroups } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import { MultiSearchCombobox } from '@/shared/components/inputs';
import { DetailCard } from '@/shared/components/layout';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
} from '@/shared/components/ui';
import { HELI_PROVIDER_GROUP_KEY } from '@/shared/utils/permissions';

interface ObjectReminderSettingsCardProps {
  control: Control<any>;
  disabled?: boolean;
  customerId?: string;
  lastSentAt?: string | null;
  enabledAt?: string | null;
  customerType?: string;
}

export function ObjectReminderSettingsCard({
  control,
  disabled = false,
  customerId,
  lastSentAt,
  enabledAt,
  customerType = 'customer',
}: ObjectReminderSettingsCardProps) {
  const { t } = useIntl();
  const enabled = useWatch({
    control,
    name: 'objectDigestEnabled',
    defaultValue: false,
  }) as boolean;
  const intervalValue = useWatch({
    control,
    name: 'objectDigestInterval',
    defaultValue: 6,
  });
  const intervalNumber =
    typeof intervalValue === 'number' ? intervalValue : Number(intervalValue ?? NaN);
  const intervalValid =
    Number.isFinite(intervalNumber) && intervalNumber >= 1 && intervalNumber <= 24;
  const lastSentDate = lastSentAt ? DateTime.fromISO(lastSentAt) : null;
  const enabledAtDate = enabledAt ? DateTime.fromISO(enabledAt) : null;
  const baseDate = lastSentDate?.isValid
    ? lastSentDate
    : enabledAtDate?.isValid
      ? enabledAtDate
      : null;
  const lastSentLabel = lastSentDate?.isValid
    ? lastSentDate.toFormat('dd.MM.yyyy')
    : t('customer.objectDigest.lastSentNever');
  const nextSendLabel = (() => {
    if (!intervalValid) return t('customer.objectDigest.nextSendUnknown');
    if (!baseDate) return t('customer.objectDigest.nextSendAfterActivation');
    return baseDate.plus({ months: intervalNumber }).toFormat('dd.MM.yyyy');
  })();

  const recipientGroupKey =
    customerType === HELI_PROVIDER_GROUP_KEY ? HELI_PROVIDER_GROUP_KEY : 'customer-admin';
  const { data: customerAdminGroups } = useUserGroups({
    where: {
      key: {
        equals: recipientGroupKey,
      },
    },
    limit: 1,
  });

  const customerAdminGroupId = customerAdminGroups?.docs?.[0]?.id as string | undefined;

  const customWhere = useMemo<Where | undefined>(() => {
    if (!customerId) return undefined;
    const conditions: Where[] = [{ customer: { equals: customerId } }];
    if (customerAdminGroupId) {
      conditions.push({ userGroup: { equals: customerAdminGroupId } });
    }
    return { and: conditions };
  }, [customerId, customerAdminGroupId]);

  const recipientsDisabled = disabled || !enabled || !customerId || !customerAdminGroupId;
  const recipientsHelperText =
    customerType === HELI_PROVIDER_GROUP_KEY
      ? t('customer.objectDigest.recipientsHelperHeliProvider')
      : t('customer.objectDigest.recipientsHelper');

  return (
    <DetailCard icon={Bell} title={t('customer.objectDigest.title')}>
      <div className="space-y-6">
        <div className="flex gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
          <Info size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
          <p>{t('customer.objectDigest.description')}</p>
        </div>

        <FormField
          control={control}
          name={'objectDigestEnabled'}
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-4">
              <div>
                <FormLabel className="mb-1">{t('customer.objectDigest.enabledLabel')}</FormLabel>
                <p className="text-xs text-muted-foreground">
                  {t('customer.objectDigest.enabledHelper')}
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {enabled && (
          <>
            <div className="space-y-4 rounded-lg border border-border bg-card px-4 py-4">
              <FormField
                control={control}
                name={'objectDigestRecipients'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.objectDigest.recipientsLabel')}</FormLabel>
                    <FormControl>
                      <MultiSearchCombobox
                        value={(field.value as string[]) ?? []}
                        onSelect={field.onChange}
                        collectionKey="users"
                        searchKey="email"
                        disabled={recipientsDisabled}
                        customWhere={customWhere}
                        texts={{
                          selectItemMsg: t('customer.objectDigest.recipientsPlaceholder'),
                          searchPlaceholder: t('customer.objectDigest.recipientsSearch'),
                        }}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">{recipientsHelperText}</p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 rounded-lg border border-border bg-card px-4 py-4">
              <FormField
                control={control}
                name={'objectDigestInterval'}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.objectDigest.intervalLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={24}
                        value={field.value ?? ''}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          field.onChange(nextValue === '' ? '' : Number(nextValue));
                        }}
                        disabled={disabled}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t('customer.objectDigest.intervalHelper')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="rounded-lg border border-border bg-muted/50 divide-y divide-border">
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <span className="text-sm font-medium">
                  {t('customer.objectDigest.lastSentLabel')}
                </span>
                <span className="text-sm text-muted-foreground">{lastSentLabel}</span>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <span className="text-sm font-medium">
                  {t('customer.objectDigest.nextSendLabel')}
                </span>
                <span className="text-sm text-muted-foreground tabular-nums">{nextSendLabel}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </DetailCard>
  );
}
